/**
 * SessionExpiryWatcher
 *
 * A lightweight component that must be rendered inside <Router> so it can
 * use useNavigate / useLocation.  It watches the AuthContext `sessionExpired`
 * flag; when it flips to true it shows a toast and redirects the user to
 * /login with the current path stored in location.state.from so Login.js
 * can restore it after a successful sign-in.
 *
 * `sessionExpired` is flipped by AuthContext whenever a SIGNED_OUT auth event
 * arrives that was NOT triggered by the user's own signOut() call — that
 * covers both a native Supabase refresh-token failure and a forced signOut()
 * from `services/mcpClient.js` after an MCP call comes back 401 (#389).
 *
 * IMPORTANT: this component must actually be mounted (see App.js) for any of
 * this to take effect — it was accidentally dropped from the render tree by
 * commit 2172a38 and the whole expiry flow was silently dead until #389.
 *
 * @module components/common/SessionExpiryWatcher
 */

import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from '../../utils/toast';
import { useAuth } from '../../contexts/AuthContext';

const SessionExpiryWatcher = () => {
  const { sessionExpired } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const handledRef = useRef(false);

  useEffect(() => {
    if (sessionExpired && !handledRef.current) {
      handledRef.current = true;
      toast.warning('Tu sesión expiró, iniciá sesión de nuevo.');
      navigate('/login', {
        state: { from: location, sessionExpired: true },
        replace: true,
      });
    }

    // Reset the handled flag when sessionExpired goes back to false
    if (!sessionExpired) {
      handledRef.current = false;
    }
  }, [sessionExpired, navigate, location]);

  return null;
};

export default SessionExpiryWatcher;
